import { computed, shallowRef, watch, type Ref } from 'vue'
import {
  fetchCartoonAppearances,
  fetchCartoonInfo,
  fetchCartoonWorks,
  followCartoon,
  unfollowCartoon,
} from '@/api/cartoon'
import type { CartoonProfileInfo, CartoonWorkItem } from '@/api/cartoon/type'
import { useUserStore } from '@/stores/user'

export type CartoonMainTab = 'works' | 'appearances'
export type CartoonWorkFilter = 'all' | 'pipe' | 'media' | 'radio'
export type CartoonUnavailableReason = 'not-found' | 'unpublished'

export const CARTOON_WORK_FILTERS: { key: CartoonWorkFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'media', label: '视频' },
  { key: 'pipe', label: '互动' },
  { key: 'radio', label: '听听' },
]

const CARTOON_PROFILE_EMPTY_PAGE_AUTOFILL_LIMIT = 3

export function useCartoonProfile(cartoonId: Ref<string>) {
  const userStore = useUserStore()
  const profile = shallowRef<CartoonProfileInfo | null>(null)
  const works = shallowRef<CartoonWorkItem[]>([])
  const appearances = shallowRef<CartoonWorkItem[]>([])
  const activeTab = shallowRef<CartoonMainTab>('works')
  const activeWorkFilter = shallowRef<CartoonWorkFilter>('all')
  const loadingProfile = shallowRef(false)
  const loadingWorks = shallowRef(false)
  const loadingAppearances = shallowRef(false)
  const loadingMore = shallowRef(false)
  const followLoading = shallowRef(false)
  const error = shallowRef<string | null>(null)
  const unavailableReason = shallowRef<CartoonUnavailableReason | null>(null)
  const listLoginRequired = shallowRef(false)
  const pageNo = shallowRef(1)
  const hasNext = shallowRef(false)
  let profileRequestId = 0
  let listRequestId = 0

  const isLoggedIn = computed(() => userStore.isLoggedIn())
  const currentItems = computed(() =>
    activeTab.value === 'works' ? works.value : appearances.value,
  )
  const isInitialLoading = computed(
    () =>
      loadingProfile.value ||
      (activeTab.value === 'works' ? loadingWorks.value : loadingAppearances.value),
  )
  const canLoadMore = computed(
    () =>
      !unavailableReason.value &&
      !loadingMore.value &&
      !loadingWorks.value &&
      !loadingAppearances.value &&
      hasNext.value,
  )
  const notFound = computed(() => unavailableReason.value === 'not-found')
  const unpublished = computed(() => unavailableReason.value === 'unpublished')

  function setActiveTab(tab: CartoonMainTab) {
    activeTab.value = tab
  }

  function setActiveWorkFilter(filter: CartoonWorkFilter) {
    activeWorkFilter.value = filter
  }

  function resetListState() {
    works.value = []
    appearances.value = []
    pageNo.value = 1
    hasNext.value = false
    loadingWorks.value = false
    loadingAppearances.value = false
    loadingMore.value = false
  }

  function setListLoginRequired() {
    resetListState()
    listLoginRequired.value = true
    userStore.resetUserInfo()
  }

  function canRequestProtectedList() {
    if (isLoggedIn.value) {
      return true
    }

    resetListState()
    listLoginRequired.value = true
    return false
  }

  async function refreshProfile() {
    const id = cartoonId.value
    const requestId = ++profileRequestId

    if (!id) {
      profile.value = null
      unavailableReason.value = null
      error.value = '缺少角色标识'
      return
    }

    loadingProfile.value = true
    error.value = null
    unavailableReason.value = null
    listRequestId += 1

    try {
      const res = await fetchCartoonInfo(id)

      if (requestId !== profileRequestId) {
        return
      }

      if (res.code === 302) {
        userStore.resetUserInfo()
        throw new Error(res.msg || '角色信息加载失败')
      }

      if (res.code === 460 || res.code === 461) {
        profile.value = null
        error.value = null
        unavailableReason.value = res.code === 460 ? 'not-found' : 'unpublished'
        resetListState()
        return
      }

      if (res.code !== 0) {
        throw new Error(res.msg || '角色信息加载失败')
      }

      unavailableReason.value = null
      profile.value = res.data.cartoon
    } catch (err) {
      if (requestId !== profileRequestId) {
        return
      }

      profile.value = null
      unavailableReason.value = null
      error.value = err instanceof Error ? err.message : '角色信息加载失败'
    } finally {
      if (requestId === profileRequestId) {
        loadingProfile.value = false
      }
    }
  }

  async function refreshCurrentList() {
    const id = cartoonId.value
    const requestId = ++listRequestId

    if (error.value || unavailableReason.value || !profile.value) {
      resetListState()
      return
    }

    if (!canRequestProtectedList()) {
      return
    }

    if (!id) {
      resetListState()
      return
    }

    error.value = null
    listLoginRequired.value = false
    pageNo.value = 1
    hasNext.value = false

    if (activeTab.value === 'works') {
      loadingWorks.value = true
      works.value = []
    } else {
      loadingAppearances.value = true
      appearances.value = []
    }

    try {
      let nextPage = 1
      let emptyPageCount = 0

      while (true) {
        const res =
          activeTab.value === 'works'
            ? await fetchCartoonWorks(id, activeWorkFilter.value, nextPage)
            : await fetchCartoonAppearances(id, nextPage)

        if (requestId !== listRequestId) {
          return
        }

        if (res.code === 302) {
          setListLoginRequired()
          return
        }

        if (res.code !== 0) {
          throw new Error(res.msg || '角色内容加载失败')
        }

        const list = res.data.list ?? []
        const responseNextPageNo = res.data.pageNo ?? nextPage
        const cursorAdvanced = responseNextPageNo !== nextPage

        if (list.length > 0 || !res.data.hasNext) {
          if (activeTab.value === 'works') {
            works.value = list
          } else {
            appearances.value = list
          }

          pageNo.value = responseNextPageNo
          hasNext.value = Boolean(res.data.hasNext)
          return
        }

        emptyPageCount += 1

        if (!cursorAdvanced || emptyPageCount >= CARTOON_PROFILE_EMPTY_PAGE_AUTOFILL_LIMIT) {
          if (activeTab.value === 'works') {
            works.value = []
          } else {
            appearances.value = []
          }

          pageNo.value = responseNextPageNo
          hasNext.value = false
          return
        }

        nextPage = responseNextPageNo
      }
    } catch (err) {
      if (requestId !== listRequestId) {
        return
      }

      if (activeTab.value === 'works') {
        works.value = []
      } else {
        appearances.value = []
      }

      error.value = err instanceof Error ? err.message : '角色内容加载失败'
    } finally {
      if (requestId === listRequestId) {
        loadingWorks.value = false
        loadingAppearances.value = false
      }
    }
  }

  async function loadMore() {
    if (
      unavailableReason.value ||
      !canRequestProtectedList() ||
      !canLoadMore.value ||
      !cartoonId.value
    ) {
      return
    }

    const requestId = ++listRequestId
    let nextPage = pageNo.value
    let emptyPageCount = 0

    loadingMore.value = true

    try {
      while (true) {
        const res =
          activeTab.value === 'works'
            ? await fetchCartoonWorks(cartoonId.value, activeWorkFilter.value, nextPage)
            : await fetchCartoonAppearances(cartoonId.value, nextPage)

        if (requestId !== listRequestId) {
          return
        }

        if (res.code === 302) {
          setListLoginRequired()
          return
        }

        if (res.code !== 0) {
          throw new Error(res.msg || '更多内容加载失败')
        }

        const currentList = activeTab.value === 'works' ? works.value : appearances.value
        const existingIds = new Set(currentList.map((item) => item.id))
        const nextItems = (res.data.list ?? []).filter((item) => !existingIds.has(item.id))
        const responseNextPageNo = res.data.pageNo ?? nextPage
        const cursorAdvanced = responseNextPageNo !== nextPage

        if (nextItems.length > 0) {
          const mergedList = currentList.concat(nextItems)

          if (activeTab.value === 'works') {
            works.value = mergedList
          } else {
            appearances.value = mergedList
          }

          pageNo.value = responseNextPageNo
          hasNext.value = Boolean(res.data.hasNext)
          return
        }

        if (!res.data.hasNext) {
          pageNo.value = responseNextPageNo
          hasNext.value = false
          return
        }

        emptyPageCount += 1

        if (!cursorAdvanced || emptyPageCount >= CARTOON_PROFILE_EMPTY_PAGE_AUTOFILL_LIMIT) {
          pageNo.value = responseNextPageNo
          hasNext.value = false
          return
        }

        pageNo.value = responseNextPageNo
        nextPage = responseNextPageNo
      }
    } catch (err) {
      if (requestId === listRequestId) {
        error.value = err instanceof Error ? err.message : '更多内容加载失败'
      }
    } finally {
      if (requestId === listRequestId) {
        loadingMore.value = false
      }
    }
  }

  async function refresh() {
    listLoginRequired.value = false

    await refreshProfile()

    if (error.value || unavailableReason.value || !profile.value) {
      resetListState()
      return
    }

    await refreshCurrentList()
  }

  async function toggleFollow() {
    if (!profile.value || followLoading.value) {
      return
    }

    if (!isLoggedIn.value) {
      listLoginRequired.value = true
      return
    }

    const current = profile.value
    const nextHasFollow = current.hasFollow === 1 ? 0 : 1

    followLoading.value = true
    profile.value = {
      ...current,
      hasFollow: nextHasFollow,
      fansNum: Math.max(0, current.fansNum + (nextHasFollow === 1 ? 1 : -1)),
    }

    try {
      const res =
        nextHasFollow === 1 ? await followCartoon(current.id) : await unfollowCartoon(current.id)

      if (res.code !== 0) {
        throw new Error(res.msg || '关注状态更新失败')
      }
    } catch (err) {
      profile.value = current
      throw err
    } finally {
      followLoading.value = false
    }
  }

  watch([cartoonId, isLoggedIn], refresh, { immediate: true })
  watch(activeTab, refreshCurrentList)
  watch(activeWorkFilter, () => {
    if (activeTab.value === 'works') {
      void refreshCurrentList()
    }
  })

  return {
    profile,
    works,
    appearances,
    currentItems,
    activeTab,
    activeWorkFilter,
    loadingProfile,
    loadingWorks,
    loadingAppearances,
    loadingMore,
    followLoading,
    error,
    unavailableReason,
    notFound,
    unpublished,
    listLoginRequired,
    hasNext,
    isInitialLoading,
    canLoadMore,
    setActiveTab,
    setActiveWorkFilter,
    refresh,
    loadMore,
    toggleFollow,
  }
}
